/**
 * Countries, as codes only.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE ARE NO NAMES IN HERE
 * ---------------------------------------------------------------------------
 *
 * A list of 249 country names would need translating into every language the
 * product ever speaks, and would then be wrong in the way such lists always go
 * wrong — stale, inconsistently spelt, and politically arguable. Every browser
 * already ships the same list, localised and maintained by someone else:
 *
 *     new Intl.DisplayNames([locale], { type: 'region' }).of('NL')
 *
 * So the code is what is stored, transmitted and validated, and the name is
 * produced at the moment of rendering in whatever language the reader is
 * using. A guest sees "Nederland" or "Netherlands" or "Pays-Bas" without a
 * single one of those strings existing in this repository.
 *
 * ---------------------------------------------------------------------------
 * WHY THE WHOLE LIST, WHEN THE PARK IS DUTCH
 * ---------------------------------------------------------------------------
 *
 * Because the alternative is refusing a guest. A park in Gelderland takes
 * bookings from Germany, Belgium, Britain, Poland — and occasionally from
 * somewhere nobody predicted. `EU` exists so a picker can put the likely
 * dozen at the top; it is an ordering hint, never a restriction.
 */

/** ISO 3166-1 alpha-2, current codes. */
const CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ '
  + 'BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM '
  + 'DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS '
  + 'GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN '
  + 'KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ '
  + 'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM '
  + 'PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV '
  + 'SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI '
  + 'VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

const VALID = new Set(CODES);

/** Every code, alphabetical. Names come from `Intl.DisplayNames` at render time. */
export const COUNTRY_CODES: readonly string[] = CODES;

/**
 * The EU, plus the near neighbours a Dutch park actually sees.
 *
 * An ordering hint for a picker, so the likely answer is two keystrokes away
 * rather than forty. Not a restriction, and not a political statement — the UK,
 * Norway and Switzerland are here because their cars turn up.
 */
export const NEARBY_COUNTRIES: readonly string[] = [
  'NL', 'BE', 'DE', 'GB', 'FR', 'LU', 'DK', 'PL', 'AT', 'CH', 'IT', 'ES', 'CZ', 'SE', 'NO',
  'IE', 'PT', 'HU', 'SK', 'SI', 'HR', 'RO', 'BG', 'GR', 'FI', 'EE', 'LV', 'LT', 'CY', 'MT',
];

/** Is this a current ISO 3166-1 alpha-2 code? Case-insensitive. */
export function isCountryCode(code: string | null | undefined): boolean {
  return typeof code === 'string' && VALID.has(code.trim().toUpperCase());
}

/** Normalise to the stored form, or empty when it is not a country. */
export function normaliseCountry(code: string | null | undefined): string {
  const up = (code ?? '').trim().toUpperCase();
  return VALID.has(up) ? up : '';
}
